import { useState, useMemo } from 'react';
import { Select, SelectProps, Loader } from '@mantine/core';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

export interface PagedResp<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
}

interface Option { value: string; label: string }

interface Props extends Omit<SelectProps, 'data' | 'onChange'> {
  /** Backend endpoint, masalan "/students/parents" (q= bilan qidiradi) */
  endpoint: string;
  /** Element -> option (value/label) */
  mapOption: (item: never) => Option;
  /** Qo'shimcha query parametrlar */
  params?: Record<string, string | undefined>;
  value: string | null;
  onChange: (value: string | null) => void;
  /** Tahrirda joriy qiymat ro'yxatda bo'lmasligi mumkin — shu option doim qo'shiladi */
  currentOption?: Option | null;
}

/**
 * Serverdan qidiruvchi Select — katta ro'yxatlar uchun.
 * Ma'lumot sahifalab olinganda hammasi clientga kelmaydi,
 * shuning uchun qidiruv har doim serverda bajariladi (debounce 300ms).
 */
export function ServerSearchSelect({ endpoint, mapOption, params, value, onChange, currentOption, ...rest }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);

  const { data, isFetching } = useQuery({
    queryKey: ['server-select', endpoint, debounced, params],
    queryFn: async () => {
      const res = await api.get(endpoint, { params: { q: debounced || undefined, limit: 20, ...params } });
      const raw = res.data as unknown;
      const items = Array.isArray(raw) ? raw : (raw as PagedResp<unknown>).items;
      return (items as never[]).map(mapOption);
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const options = useMemo(() => {
    const list = data ?? [];
    if (currentOption && !list.some((o) => o.value === currentOption.value)) {
      return [currentOption, ...list];
    }
    return list;
  }, [data, currentOption]);

  return (
    <Select
      searchable
      clearable
      data={options}
      value={value}
      onChange={onChange}
      searchValue={search}
      onSearchChange={setSearch}
      nothingFoundMessage={isFetching ? t('common.searching') : t('common.noResults')}
      rightSection={isFetching ? <Loader size={14} /> : undefined}
      // Server qidiradi — client filtri o'chirilgan
      filter={({ options: opts }) => opts}
      {...rest}
    />
  );
}
